/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useState } from "react";
import { onValue, ref } from "firebase/database";
import { db } from "@/lib/firebase";
import type { SafeHaven } from "@/types/safecircle";

// Legacy seed data can store lat/lng as strings. Coerce here so a strict
// `typeof === "number"` filter doesn't silently drop every marker on native.
function toFiniteNumber(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface UseSafeHavensResult {
  havens: SafeHaven[];
  loading: boolean;
  error: Error | null;
}

export function useSafeHavens(
  centerLat: number | null,
  centerLng: number | null,
  radiusKm = 5,
): UseSafeHavensResult {
  const [havens, setHavens] = useState<SafeHaven[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (centerLat == null || centerLng == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const havensRef = ref(db, "safeHavens");
    const unsub = onValue(
      havensRef,
      (snap) => {
        const val = (snap.val() as Record<string, Omit<SafeHaven, "id">> | null) ?? {};
        const list: SafeHaven[] = Object.entries(val)
          .map(([id, h]) => {
            const lat = toFiniteNumber(h.lat);
            const lng = toFiniteNumber(h.lng);
            if (lat === null || lng === null) return null;
            return { ...h, id, lat, lng } as SafeHaven;
          })
          .filter(
            (h): h is SafeHaven =>
              h !== null &&
              h.status !== "rejected" &&
              haversineKm(centerLat, centerLng, h.lat, h.lng) <= radiusKm,
          )
          .sort(
            (a, b) =>
              haversineKm(centerLat, centerLng, a.lat, a.lng) -
              haversineKm(centerLat, centerLng, b.lat, b.lng),
          );
        setHavens(list);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [centerLat, centerLng, radiusKm]);

  return { havens, loading, error };
}
